// Talks to the acrylic-content-api Lambda. The base URL comes from an env var
// that is baked in at build time (see REACT_APP_CONTENT_API_URL).
const API_URL = (process.env.REACT_APP_CONTENT_API_URL || "").replace(/\/$/, "");
const TOKEN_KEY = "content-admin-token";
const ROLE_KEY = "content-admin-role";

export class ApiError extends Error {
     constructor(status, message) {
          super(message);
          this.status = status;
     }
}

// sessionStorage clears when the tab closes, so the admin is logged out automatically.
export const getToken = () => {
     try {
          return sessionStorage.getItem(TOKEN_KEY);
     } catch {
          return null;
     }
};
export const setToken = (token) => {
     try {
          sessionStorage.setItem(TOKEN_KEY, token);
     } catch {
          /* storage blocked: client needs to log in again after a refresh */
     }
};
// "client" or "master". Only used to decide what to show; the server checks it for real.
export const getRole = () => {
     try {
          return sessionStorage.getItem(ROLE_KEY);
     } catch {
          return null;
     }
};
export const setRole = (role) => {
     try {
          sessionStorage.setItem(ROLE_KEY, role);
     } catch {
          /* ignore */
     }
};
export const clearToken = () => {
     try {
          sessionStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(ROLE_KEY);
     } catch {
          /* nothing to clear */
     }
};

async function request(path, { method = "GET", body, auth = false } = {}) {
     const headers = {};
     if (body) headers["Content-Type"] = "application/json";
     if (auth) headers.Authorization = `Bearer ${getToken()}`;

     let res;
     try {
          res = await fetch(`${API_URL}${path}`, {
               method,
               headers,
               body: body ? JSON.stringify(body) : undefined,
          });
     } catch {
          throw new ApiError(0, "Couldn't reach the server. Check your connection and try again.");
     }
     const data = await res.json().catch(() => null);
     if (!res.ok) throw new ApiError(res.status, (data && data.error) || "Something went wrong.");
     if (data === null) {
          throw new ApiError(
               res.status,
               "The server sent an unexpected reply. Check that REACT_APP_CONTENT_API_URL is set."
          );
     }
     return data;
}

export const login = (client, password) =>
     request("/login", { method: "POST", body: { client, password } });

export const getConfig = (client) => request(`/config/${client}`);

export const listEntries = (client, type) =>
     request(`/content/${client}/${type}?all=1`, { auth: true });

export const createEntry = (client, type, body) =>
     request(`/content/${client}/${type}`, { method: "POST", body, auth: true });

// Editing is a POST to the entry's own address, which the API already allows.
export const updateEntry = (client, type, id, body) =>
     request(`/content/${client}/${type}/${id}`, { method: "POST", body, auth: true });

export const deleteEntry = (client, type, id) =>
     request(`/content/${client}/${type}/${id}`, { method: "DELETE", auth: true });

// Two steps: ask the Lambda for a short-lived upload link, then send the file
// straight to S3. Returns the S3 key, which goes into the entry when it's saved.
export async function uploadFile(client, type, fieldKey, file, title) {
     const { key, uploadUrl } = await request(`/upload-url/${client}/${type}`, {
          method: "POST",
          body: { fieldKey, fileName: file.name, contentType: file.type, title },
          auth: true,
     });
     let res;
     try {
          res = await fetch(uploadUrl, {
               method: "PUT",
               headers: { "Content-Type": file.type },
               body: file,
          });
     } catch {
          throw new ApiError(0, `Couldn't upload ${file.name}. Check your connection and try again.`);
     }
     if (!res.ok) throw new ApiError(res.status, `Couldn't upload ${file.name}. Try again.`);
     return key;
}

export const changePassword = (client, currentPassword, newPassword) =>
     request(`/password/${client}`, {
          method: "POST",
          body: { currentPassword, newPassword },
          auth: true,
     });